/**
 * What a purchase or top-up answered (TMMAPP-0113 §C 7, as amended by TMMAPI-0151): one line of copy
 * and, where the person can act, its action — UPDATE CARD / ADD CARD (Settings → Billing → Card on
 * file), Refresh after a slow pending charge, or the subscribe card in place for a `403
 * SUBSCRIPTION_REQUIRED`. Every message is announced politely.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReferenceLine } from '@/components/ReferenceLine';
import { ChangeCardForm } from '@/features/billing/ChangeCardForm';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useBillingProfile } from '@/features/settings/billingProfileContext';
import { settingsHref } from '@/features/settings/settingsHref';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { declineKey, type AiWalletChargeOutcome } from './useAiWalletCharge';

interface ChargeOutcomeNoticeProps {
  outcome: AiWalletChargeOutcome;
  onRefresh: () => void;
  /** Beside the success line (the prompt's Try again); nothing reruns on its own. */
  successAction?: ReactNode;
  /** The modal this notice sits in: the subscribe card closes it first (DLG-005). */
  onCloseHost?: (() => void) | undefined;
  /**
   * The in-place card update is in flight (TMMAPP-0119 item 16): its host holds its close, so Esc
   * or Cancel can't drop the update and the charge's retry.
   */
  onBusyChange?: ((busy: boolean) => void) | undefined;
}

/**
 * `422 TAX_LOCATION_REQUIRED` (TMMAPP-0119 item 11): nothing was charged. Update card opens the
 * change-card form in place (inside a modal too, DLG-005: never a dialog over its host); once the
 * card is updated, the same charge runs once more with a new Idempotency-Key.
 */
function TaxLocationNotice({
  retry,
  onBusyChange,
}: {
  retry: () => void;
  onBusyChange?: ((busy: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const [updating, setUpdating] = useState(false);
  const { reload: reloadBilling } = useBillingProfile();
  return (
    <Stack spacing={1.5} data-tax-location-notice="">
      <Alert
        severity="warning"
        role="status"
        aria-live="polite"
        action={
          updating ? null : (
            <Button color="inherit" size="small" onClick={() => setUpdating(true)}>
              {t('aiWallet.updateCard')}
            </Button>
          )
        }
      >
        {t('billing.taxLocationRequired')}
      </Alert>
      {updating && (
        <ChangeCardForm
          {...(onBusyChange !== undefined ? { onBusyChange } : {})}
          onUpdated={() => {
            retry();
            // On Settings the page's billing profile re-reads, so its tax-location banner clears
            // (a no-op outside the provider).
            void reloadBilling();
          }}
        />
      )}
    </Stack>
  );
}

export function ChargeOutcomeNotice({
  outcome,
  onRefresh,
  successAction,
  onCloseHost,
  onBusyChange,
}: ChargeOutcomeNoticeProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (outcome.kind === 'idle' || outcome.kind === 'busy') return null;
  if (outcome.kind === 'subscriptionRequired') return <SubscribeInPlace onClose={onCloseHost} />;
  if (outcome.kind === 'taxLocationRequired') {
    return <TaxLocationNotice retry={outcome.retry} onBusyChange={onBusyChange} />;
  }

  const cardLink = (label: string) => (
    <Button component={RouterLink} to={settingsHref('subscription')} color="inherit" size="small">
      {label}
    </Button>
  );

  let severity: 'success' | 'info' | 'warning' = 'warning';
  let text: string;
  let action: ReactNode = null;
  switch (outcome.kind) {
    case 'added': {
      severity = 'success';
      const { currency } = outcome;
      // Sentence interpolations only, never an amount node.
      const money = (minor: number) => formatMoneyMinor(minor, currency, locale);
      // The served tax and total (TMMAPI-0156 U2: Stripe Tax's, on the charge); without, the amount.
      text =
        outcome.amountMinor === null
          ? t('aiWallet.addedPlain')
          : outcome.taxMinor !== null && outcome.totalMinor !== null && outcome.taxMinor > 0
            ? t('aiWallet.addedTaxed', {
                amount: money(outcome.amountMinor),
                total: money(outcome.totalMinor),
                tax: money(outcome.taxMinor),
              })
            : t('aiWallet.added', { amount: money(outcome.amountMinor) });
      action = successAction ?? null;
      break;
    }
    case 'processing':
      severity = 'info';
      text = t('aiWallet.processing');
      break;
    case 'processingSlow':
      severity = 'info';
      text = t('aiWallet.processing');
      action = (
        <Button color="inherit" size="small" onClick={onRefresh}>
          {t('aiWallet.refresh')}
        </Button>
      );
      break;
    case 'declined':
      text = t(declineKey(outcome.failureCode));
      action = cardLink(t('aiWallet.updateCard'));
      break;
    case 'actionFailed':
      text = t('card.challengeFailed');
      break;
    case 'noCard':
      text = t('aiWallet.noCard');
      action = cardLink(t('aiWallet.addCard'));
      break;
    case 'unavailable':
      text = t('aiWallet.chargeUnavailable');
      break;
    case 'conflict':
      text = t('aiWallet.conflict');
      break;
    // TMMAPI-0159 (TMMAPP-0122 item 23): neither has a Retry; nothing reruns on its own.
    case 'disputeHold':
      text = t('aiWallet.disputeHold');
      break;
    case 'billingConfiguration':
      text = t('billing.configurationError');
      break;
    case 'error':
      text = t('aiWallet.errorGeneric');
      break;
  }
  return (
    <Alert severity={severity} action={action} role="status" aria-live="polite">
      {text}
      {outcome.kind === 'billingConfiguration' && outcome.requestId !== null && (
        <ReferenceLine requestId={outcome.requestId} ink="inherit" />
      )}
    </Alert>
  );
}
